import React, { createRef, FunctionComponent, MouseEventHandler, useEffect, useState } from "react";

import {
    AnimatedMount,
    AnimationType,
    BooleanSwitcher,
    BooleanSwitcherChildrenProps,
    FocusProvider,
    FocusProviderPropsChildrenProps,
    Icon,
    IconColor,
    IconSize,
} from "@multiweb/reactComponents";
import { ANIMATION_DURATION_MS, bemClassNames, TAB_INDEX_FOCUSABLE_ONLY, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE, useSetFocus } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { AccessibilityProps } from "@multiweb/types/react";

export type SearchInputComponentOnChange = (value: string) => void;

export type FormSearchInputComponentProps = {
    /** Callback volaný při změně hodnoty vyhledávacího pole */
    onChange: SearchInputComponentOnChange;
    /** Callback volaný při kliknutí. */
    onClick?: MouseEventHandler<HTMLInputElement>;
    /** Hodnota zobrazovaná při prázdném vstupním poli */
    placeholder?: string;
    /** Aktuální hodnota vyhledávacího pole */
    value?: string;
    /** Příznak, zda je komponenta zaměřená. */
    isFocused: boolean;
    /** Příznak, zda se má při zaměření vybrat celý text. */
    selectAllText: boolean;
    /** Zda odstraňovat krajní mezery ve výsledném řetězci. */
    trimSpaces?: boolean;
} & Pick<AccessibilityProps, "tabIndex">;

const classNames = bemClassNames("form-search-input-component");

/** Komponenta slouží jako textové pole pro vyhledávání formulářů v hlavním menu aplikace. */
export const FormSearchInputComponent: FunctionComponent<FormSearchInputComponentProps> = ({
    onChange,
    onClick,
    placeholder,
    value = "",
    isFocused,
    selectAllText,
    tabIndex = TAB_INDEX_FOCUSABLE_TAB_SEQUENCE,
    trimSpaces = true,
}) => {
    // Stav se využívá pro podmíněné zobrazení tlačítka pro smazání inputu.
    const [valueState, setValueState] = useState<string>(value);

    // UseEffect zde umožňuje nastavit hodnotu inputu zvenčí.
    useEffect(() => {
        value && setValueState(value);
    }, [value]);

    const inputElement = createRef<HTMLInputElement>();
    useSetFocus<HTMLInputElement>(isFocused, inputElement, undefined, selectAllText);

    /**
     * Metoda pro nastavení hodnoty inputu a jeho případné zaměření
     * @param value nová hodnota inputu
     * @param focus zda-li se má po nastavení hodnoty input zaměřit
     */
    const setInput = (value: string, focus?: boolean) => {
        setValueState(value);
        if (trimSpaces) {
            value = value.trim(); // Odstraníme krajní mezery
        }
        onChange(value);
        focus && inputElement.current?.focus();
    };

    return (
        <FocusProvider tabIndex={TAB_INDEX_FOCUSABLE_ONLY}>
            {({ isFocused }: FocusProviderPropsChildrenProps) => (
                <div className={classNames({ focused: isFocused })}>
                    <span className={classNames("search-icon")}>
                        <Icon
                            iconCode={IconCode.uiSearch}
                            color={IconColor.whiteFixed}
                            size={IconSize.small}
                        />
                    </span>
                    <input
                        type="text"
                        className={classNames("input")}
                        onChange={(e) => setInput(e.target.value)}
                        spellCheck={false}
                        ref={inputElement}
                        autoComplete="one-time-code"
                        aria-autocomplete="none"
                        value={valueState}
                        {...{ placeholder, onClick, tabIndex }}
                    />
                    <div className={classNames("clear-button")}>
                        <AnimatedMount
                            mounted={!!valueState}
                            animationType={AnimationType.opacity}
                            animationDurationMs={ANIMATION_DURATION_MS}
                        >
                            <BooleanSwitcher>
                                {({ value: isHovered, turnOn: handleHover, turnOff: handleBlur }: BooleanSwitcherChildrenProps) => (
                                    <button
                                        onClick={() => setInput("", true)}
                                        onMouseEnter={handleHover}
                                        onMouseLeave={handleBlur}
                                        onFocus={handleHover}
                                        onBlur={handleBlur}
                                    >
                                        <Icon
                                            iconCode={IconCode.uiCrossSmall}
                                            color={isHovered ? IconColor.whiteFixed : IconColor.grey1}
                                            size={IconSize.small}
                                        />
                                    </button>
                                )}
                            </BooleanSwitcher>
                        </AnimatedMount>
                    </div>
                </div>
            )}
        </FocusProvider>
    );
};
